import React from 'react';
import { Box, Tooltip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

interface CountDisplayProps {
       exceptionCount: number;
       alertsCount: number;
       notesCount: number;
       generalCommentsCount: number;
       showTooltip?: boolean;
       size?: 'small' | 'medium' | 'large';
       variant?: 'compact' | 'spaced';
       hideExceptionsAndAlerts?: boolean;
}

const COUNT_ICONS = {
       exceptions: WarningAmberIcon,
       alerts: ErrorOutlineIcon,
       notes: ChatBubbleOutlineIcon,
} as const;

const COUNT_COLORS = {
       exceptions: '#C93939',
       alerts: '#c58026',
       notes: '#3f63c0',
} as const;

const SIZE_CONFIG = {
       small: { fontSize: '0.75rem', iconSize: '0.875rem' },
       medium: { fontSize: '0.875rem', iconSize: '1rem' },
       large: { fontSize: '1rem', iconSize: '1.125rem' },
} as const;

export const CountDisplay: React.FC<CountDisplayProps> = ({ exceptionCount, alertsCount, notesCount, generalCommentsCount, showTooltip = true, size = 'medium', variant = 'compact', hideExceptionsAndAlerts = false}) => {
       // Include general comments in the total notes count
       const totalNotesCount = notesCount + generalCommentsCount;
       const hasAnyCounts = exceptionCount > 0 || alertsCount > 0 || totalNotesCount > 0;
       
       // Adjust tooltip text based on whether we're hiding exceptions and alerts
       const tooltipText = hideExceptionsAndAlerts 
              ? `${totalNotesCount} notes (${generalCommentsCount} general comments)`
              : `${exceptionCount} exceptions, ${alertsCount} alerts, ${totalNotesCount} notes (${generalCommentsCount} general comments)`;
       
       const sizeConfig = SIZE_CONFIG[size];

       const CountItem: React.FC<{ count: number; type: keyof typeof COUNT_ICONS }> = ({ count, type }) => {
              const IconComponent = COUNT_ICONS[type];

              return (
                     <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                            {count}
                            <IconComponent sx={{ color: COUNT_COLORS[type], fontSize: sizeConfig.iconSize }} />
                     </Box>
              );
       };

       const countContent = (
              <Box
                     component="span"
                     sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: variant === 'spaced' ? 1 : 0.5,
                            fontSize: sizeConfig.fontSize,
                            color: hasAnyCounts ? 'text.primary' : 'text.secondary',
                            fontWeight: hasAnyCounts ? 400 : 200,
                            opacity: hasAnyCounts ? 1 : 0.7
                     }}
                     aria-label={tooltipText}
              >
                     {!hideExceptionsAndAlerts && <CountItem count={exceptionCount} type="exceptions" />}
                     {!hideExceptionsAndAlerts && <CountItem count={alertsCount} type="alerts" />}
                     <CountItem count={totalNotesCount} type="notes" />
              </Box>
       );

       if (showTooltip) {
              return (
                     <Tooltip title={tooltipText} arrow placement="top">
                            {countContent}
                     </Tooltip>
              );
       }

       return countContent;
};

export default CountDisplay; 